import { Input } from "@/components/ui/input";

/**
 * A fixed vocabulary as toggle chips: mediums, relationships. The list comes
 * from the seeded form definition, so an admin editing the choices in
 * /basement/ reaches every form that uses it without a release.
 *
 * A fixed list has to end in "Other", and "Other" is a dead end without
 * somewhere to say what it was. Pass `other` and `onOtherChange` and the box
 * appears with that chip and goes with it. It lives here rather than in each
 * of the four callers, so it cannot be forgotten on one of them.
 */
export const OTHER = "Other";

export function ChoiceChips({
  options,
  value,
  onChange,
  id,
  other,
  onOtherChange,
  onOtherBlur,
  otherLabel = 'If you selected "other", please detail here',
}: {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  id?: string;
  other?: string;
  onOtherChange?: (next: string) => void;
  /** For the forms that save on change rather than on submit: the text is
   *  written when the box is left, not once per keystroke. */
  onOtherBlur?: () => void;
  otherLabel?: string;
}) {
  const showOther = Boolean(onOtherChange) && value.includes(OTHER);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" id={id}>
        {options.map((option) => {
          const on = value.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={on}
              onClick={() =>
                onChange(on ? value.filter((v) => v !== option) : [...value, option])
              }
              className={
                on
                  ? "bg-primary text-primary-foreground rounded-md px-2.5 py-1 text-xs"
                  : // Foreground, not muted: an unchosen option is still an
                    // option somebody has to read to choose it.
                    "bg-muted text-foreground hover:bg-accent rounded-md px-2.5 py-1 text-xs"
              }
            >
              {option}
            </button>
          );
        })}
      </div>
      {showOther && (
        <Input
          aria-label={otherLabel}
          placeholder={otherLabel}
          value={other ?? ""}
          onChange={(e) => onOtherChange?.(e.target.value)}
          onBlur={onOtherBlur}
        />
      )}
    </div>
  );
}
